// components/screener/AddFilterDialog.jsx
import React, { useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, InputAdornment, IconButton, Button, Box, Typography,
    Chip, Checkbox, useMediaQuery
} from '@mui/material';
import {
    Search as SearchIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import { FILTER_CATEGORIES } from './screenerConfig';

const AddFilterDialog = ({ open, onClose, activeFilterIds, onToggleFilter }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');

    const activeCount = activeFilterIds.size;

    // Filter categories and their filters by search term
    const filteredCategories = useMemo(() => {
        const term = searchTerm.toLowerCase();
        if (!term) return FILTER_CATEGORIES;

        return FILTER_CATEGORIES
            .map(cat => ({
                ...cat,
                filters: cat.filters.filter(f => f.label.toLowerCase().includes(term)),
            }))
            .filter(cat => cat.filters.length > 0);
    }, [searchTerm]);

    const totalFilters = FILTER_CATEGORIES.reduce((sum, cat) => sum + cat.filters.length, 0);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(10, 25, 41, 0.97)'
                        : 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(12px)',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 3,
                    overflow: 'hidden',
                    maxHeight: '80vh',
                },
            }}
        >
            {/* Header */}
            <DialogTitle sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 2, px: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                        Select screener filters
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.85rem' }}>
                        ({totalFilters} total)
                    </Typography>
                    {activeCount > 0 && (
                        <Chip
                            label={`${activeCount} active`}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                        />
                    )}
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Search */}
                <Box sx={{ px: 3, pt: 2, pb: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            ...(searchTerm && {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                fontSize: '0.9rem',
                                color: theme.palette.text.primary,
                                '& fieldset': { borderColor: theme.palette.divider },
                            },
                        }}
                    />
                </Box>

                {/* Sectioned filter list */}
                <Box sx={{ overflow: 'auto', maxHeight: 'calc(80vh - 200px)', px: 3, py: 2 }}>
                    {filteredCategories.length === 0 && (
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                No filters match "{searchTerm}"
                            </Typography>
                        </Box>
                    )}

                    {filteredCategories.map((category) => (
                        <Box key={category.id} sx={{ mb: 3 }}>
                            {/* Section header */}
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    color: theme.palette.text.primary,
                                    mb: 1,
                                    pb: 0.75,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                {category.label}
                            </Typography>

                            {/* Filter items — multi-column grid */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                                gap: 0,
                            }}>
                                {category.filters.map((filter) => {
                                    const isActive = activeFilterIds.has(filter.id);

                                    return (
                                        <Box
                                            key={filter.id}
                                            onClick={() => onToggleFilter(filter)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                py: 0.75,
                                                px: 1,
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                transition: 'background 0.12s',
                                                '&:hover': {
                                                    bgcolor: theme.palette.action.hover,
                                                },
                                            }}
                                        >
                                            <Checkbox
                                                checked={isActive}
                                                size="small"
                                                sx={{
                                                    p: 0.25,
                                                    color: theme.palette.text.secondary,
                                                    '&.Mui-checked': {
                                                        color: theme.palette.primary.main,
                                                    },
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: '0.85rem',
                                                    fontWeight: isActive ? 600 : 400,
                                                    color: isActive
                                                        ? theme.palette.primary.main
                                                        : theme.palette.text.primary,
                                                }}
                                            >
                                                {filter.label}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </DialogContent>

            <DialogActions sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                px: 3, py: 2,
                justifyContent: 'space-between',
            }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.82rem' }}>
                    {activeCount} filter{activeCount !== 1 ? 's' : ''} selected
                </Typography>
                <Button onClick={onClose} variant="contained" size="small" sx={{ px: 3 }}>
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddFilterDialog;